import { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  slug: 'how-to-test-fordje-data',
  title: "How to Test Fordje's Data",
  excerpt: "A step-by-step guide to validating Fordje's data accuracy during your proof-of-concept. Two approaches for teams with or without existing AHJ databases.",
  content: `
    <p>You've seen what Fordje can do. Now you want to know: is the data actually accurate for the cities and requirements that matter to my business?</p>
    
    <p>Good. That's exactly the right question.</p>
    
    <p>This guide walks you through two approaches to validate Fordje's data during your proof-of-concept. Pick the one that fits how your team currently tracks local requirements—or use both.</p>
    
    <h2>Before You Start</h2>
    
    <p>For your POC, you'll have access to both the <strong>Synthesize tool</strong> (structured data across multiple cities) and the <strong>Research tool</strong> (deep dives into specific questions with links to source documents).</p>
    
    <p>We recommend starting with 3-5 cities you know well and 10-20 data points that matter to your projects. This gives you enough breadth to see patterns without getting lost in the weeds.</p>
    
    <h2>Option 1: Compare Against Your Existing Data</h2>
    
    <p><strong>Best for:</strong> Teams with an existing database of code requirements, CRM, or documented code requirements</p>
    
    <h3>The process</h3>
    
    <p><strong>Pick your test cities.</strong> Choose 3-5 jurisdictions where you have solid existing data—ideally a mix of cities where projects have gone smoothly and cities where you've hit surprises.</p>
    
    <p><strong>Select your data points.</strong> In the Synthesize tool, choose 10-20 data points you already track. Common starting points include setbacks, fire pathway requirements, permit fees, height limits, and zoning restrictions.</p>
    
    <p><strong>Download and compare.</strong> Export the Fordje data and line it up against what you currently have. Look for:</p>
    
    <ul>
      <li>Direct matches (confidence builder)</li>
      <li>Discrepancies where Fordje shows something different</li>
      <li>Data points Fordje has that you don't currently track</li>
    </ul>
    
    <p><strong>Investigate discrepancies.</strong> This is where it gets useful. When you find a mismatch, use the Research tool to dig into the underlying code. Fordje will point you to the source document so you can verify yourself.</p>
    
    <p>Most discrepancies fall into a few categories:</p>
    
    <ul>
      <li><strong>Your data was outdated.</strong> Codes change; Fordje caught an update you missed.</li>
      <li><strong>Formatting difference.</strong> The information is the same, just presented differently.</li>
      <li><strong>Edge case.</strong> Your data reflected a specific situation that's not the general rule.</li>
    </ul>
    
    <p><strong>Think about past projects.</strong> For cities where you've had permit issues or rework, check whether Fordje's data would have caught the problem earlier. This is often the clearest signal of value.</p>
    
    <h3>What you're looking for</h3>
    
    <p>Fordje should match or improve on your existing data at least 90% of the time. More importantly: would having this data earlier have prevented delays on past projects?</p>
    
    <h2>Option 2: Build From Your Project Questions</h2>
    
    <p><strong>Best for:</strong> Teams without a formal requirements database, or those who want to test Fordje against real workflow questions</p>
    
    <h3>The process</h3>
    
    <p><strong>List your common questions.</strong> What do you need to know about a city before you can move forward? Write down 10-20 questions your team asks repeatedly. Examples:</p>
    
    <ul>
      <li>What are the roof setback requirements?</li>
      <li>Is a permit required for battery storage in a garage?</li>
      <li>What's the maximum system size allowed?</li>
      <li>What are the fire department access requirements?</li>
      <li>What fees should we expect?</li>
    </ul>
    
    <p><strong>Test in the Research tool.</strong> Pick 3-5 cities and run your questions through Research. Drill down as needed—the tool will show you where the answers come from in the underlying code.</p>
    
    <p><strong>Evaluate the answers.</strong> Are they clear enough to act on? Do they match what you'd expect based on your experience in those markets?</p>
    
    <p><strong>Request structured data points.</strong> Once you've validated that Research gives you what you need, let us know which questions should become standard data points in Synthesize. We can typically add new data points within 24 hours.</p>
    
    <p><strong>Test across cities.</strong> With your custom data points in Synthesize, pull them across multiple cities. Check whether the format works for your workflow—do you need yes/no answers, numeric values, or more context?</p>
    
    <h3>What you're looking for</h3>
    
    <p>Can Fordje answer the questions you actually ask, in a format that fits how your team works? If something's unclear or needs adjustment, tell us—that's what the POC is for.</p>
    
    <h2>A Note on Formatting</h2>
    
    <p>Data accuracy is one thing. Data usability is another.</p>
    
    <p>During your POC, pay attention to how answers are formatted. Maybe you need a simple yes/no with exceptions noted. Maybe you need the full context. Maybe you need numeric values you can plug into a model.</p>
    
    <p>If the data is accurate but not formatted the way you need it, let us know. We can adjust how data points are structured so they actually fit your workflow.</p>
    
    <h2>Questions?</h2>
    
    <p>If you hit a wall or want help thinking through your test approach, reach out. We'd rather you test thoroughly and find any gaps now than discover them after you've committed.</p>
    
    <p>And if you find something that doesn't look right—tell us. That's how we keep getting better.</p>
  `,
  coverImage: '/lovable-uploads/how-to-test-fordje-data-cover.png',
  date: 'January 6, 2026',
  readTime: 5,
  categories: ['How To', 'Product'],
  author: {
    name: 'Richie Rasor',
    title: 'Co-Founder & COO',
    avatar: '/lovable-uploads/26c946ec-4296-4ac9-9351-78321594ea25.png'
  }
};
